import React from 'react';
import styled from 'styled-components';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
`;

const ErrorCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  max-width: 500px;
  width: 100%;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.error};
  opacity: 0.7;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorTitle = styled.h2`
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.lg};
  line-height: 1.6;
`;

const ActionButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin: 0 auto;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accentHover};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container>
          <ErrorCard>
            <ErrorIcon>
              <ExclamationTriangleIcon />
            </ErrorIcon>
            <ErrorTitle>Something went wrong</ErrorTitle>
            <ErrorMessage>
              We encountered an unexpected error. This might be due to a navigation issue or authentication problem.
              Please try refreshing the page or going back to the home screen.
            </ErrorMessage>
            <ActionButton onClick={this.handleGoHome}>
              <ArrowLeftIcon />
              Go to Home
            </ActionButton>
          </ErrorCard>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 